import backendHttpClient from './client/backendHttpClient';
import apiErrorHandler from './error/apiErrorHandler';
import { HttpClient } from './client/httpClient';
import { ServerModel } from '../models/serverModel';
import { ErrorHandler } from './error/errorHandler';
import { ServerResponse } from './responses/serverResponse';
import { DeploymentModel, ServerReloadStatus } from '../models/deploymentModel';
import { DeploymentResponse } from './responses/deploymentResponse';

class ServerApi {
  constructor(private readonly httpClient: HttpClient,
              private readonly errorHandler: ErrorHandler) {}

  public fetchAll(): Promise<ServerModel[]> {
    return this.httpClient.get<ServerResponse[]>('/servers')
      .then(response => response.map(it => this.mapToServerModel(it)))
      .catch(error => this.errorHandler.handleError(error, 'Failed to fetch all servers'));
  }

  public fetchServerById(serverId: string): Promise<ServerModel> {
    return this.httpClient.get<ServerResponse>(`/servers/${serverId}`)
      .then(response => this.mapToServerModel(response))
      .catch(error => this.errorHandler.handleError(error, 'Failed to fetch server by name'));
  }

  private mapToServerModel(response: ServerResponse): ServerModel {
    let lastDeployment: DeploymentModel | undefined;
    const { lastDeployment: lastDeploymentResponse } = response;

    if (lastDeploymentResponse) {
      const { status: deploymentStatusResponse } = lastDeploymentResponse;

      lastDeployment = {
        id: lastDeploymentResponse.id,
        serverId: lastDeploymentResponse.serverId,
        commitId: lastDeploymentResponse.commitId,
        status: {
          state: deploymentStatusResponse.state,
          processId: deploymentStatusResponse.processId,
          createdDate: deploymentStatusResponse.createdDate,
          errorMessage: deploymentStatusResponse.errorMessage,
          stackTrace: deploymentStatusResponse.stackTrace,
        },
        serverReloads: this.mapToServerReloads(lastDeploymentResponse),
        author: lastDeploymentResponse.author,
        createdDate: lastDeploymentResponse.createdDate,
        lastModifiedDate: lastDeploymentResponse.lastModifiedDate,
      };
    }

    return {
      id: response.id,
      displayName: response.displayName,
      lastDeployment,
    };
  }

  private mapToServerReloads(response: DeploymentResponse): ServerReloadStatus[] {
    return response.serverReloads.map(serverReload => ({
      reloadMethod: serverReload.reloadMethod,
      createdDate: serverReload.createdDate,
      success: serverReload.success,
      errorMessage: serverReload.errorMessage,
    }));
  }
}

export default new ServerApi(backendHttpClient, apiErrorHandler);
